import BomateWordmark from './icons/BomateWordmark';
import CampaignCard from './account/CampaignCard';
import SidebarCreditBalance from './account/SidebarCreditBalance';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { setSelectedDesktopClient } from '../store/slices/coworkSlice';
import { clearActiveSkills } from '../store/slices/skillSlice';
import { coworkService } from '../services/cowork';
import { relayService, getTargetClient, platformLabel } from '../services/relay';
import { i18nService } from '../services/i18n';
import CoworkSessionList from './cowork/CoworkSessionList';
import CoworkSearchModal from './cowork/CoworkSearchModal';
import SidebarToggleIcon from './icons/SidebarToggleIcon';
import { Cog6ToothIcon, UserCircleIcon, MagnifyingGlassIcon, XMarkIcon, ArrowDownTrayIcon, ChevronDownIcon, CloudIcon, ComputerDesktopIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import type { AuthState } from '../types/auth';
import { maskPhoneNumber, maskUsername } from '../utils/accountIdentity';

interface SidebarProps {
  /** 打开 BoMate 主会话（专属秘书，长期记忆）。 */
  onOpenSecretary?: () => void;
  onShowSettings: () => void;
  onShowAccount?: () => void;
  activeView: 'cowork' | 'skills' | 'scheduledTasks' | 'scenarios' | 'connectors' | 'coding' | 'appDownload';
  onShowSkills: () => void;
  onShowCowork: () => void;
  onShowScheduledTasks: () => void;
  onShowScenarios: () => void;
  onShowConnectors: () => void;
  onNewChat: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  hasUpdate?: boolean;
  onUpdateClick?: () => void;
  authState?: AuthState | null;
  isMobile?: boolean;
}

const SIDEBAR_WIDTH_STORAGE_KEY = 'boclaw-sidebar-width';
const SIDEBAR_DEFAULT_WIDTH = 320;
const SIDEBAR_MIN_WIDTH = 240;
const SIDEBAR_MAX_WIDTH = 480;
const SIDEBAR_MIN_CONTENT_WIDTH = 360;

const clampSidebarWidth = (width: number): number => {
  const viewportMax = typeof window === 'undefined'
    ? SIDEBAR_MAX_WIDTH
    : Math.max(SIDEBAR_MIN_WIDTH, window.innerWidth - SIDEBAR_MIN_CONTENT_WIDTH);
  return Math.min(Math.max(width, SIDEBAR_MIN_WIDTH), SIDEBAR_MAX_WIDTH, viewportMax);
};

const getInitialSidebarWidth = (): number => {
  if (typeof window === 'undefined') return SIDEBAR_DEFAULT_WIDTH;
  try {
    const storedWidth = Number(window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY));
    return Number.isFinite(storedWidth) && storedWidth > 0
      ? clampSidebarWidth(storedWidth)
      : clampSidebarWidth(SIDEBAR_DEFAULT_WIDTH);
  } catch {
    return clampSidebarWidth(SIDEBAR_DEFAULT_WIDTH);
  }
};

const NAV_ICON_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  className: 'h-[18px] w-[18px] shrink-0',
};

const NewChatIcon = () => <svg {...NAV_ICON_PROPS}><path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" /></svg>;
const ClockIcon = () => <svg {...NAV_ICON_PROPS}><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15.5 14" /></svg>;
const SkillsIcon = () => <svg {...NAV_ICON_PROPS}><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" /></svg>;
const ScenariosIcon = () => <svg {...NAV_ICON_PROPS}><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>;
const ConnectorIcon = () => <svg {...NAV_ICON_PROPS}><path d="M10 13a5 5 0 0 0 7.07 0l2.12-2.12a5 5 0 0 0-7.07-7.07L10.9 5.03" /><path d="M14 11a5 5 0 0 0-7.07 0L4.81 13.12a5 5 0 0 0 7.07 7.07l1.22-1.22" /></svg>;

const CompactNavItem: React.FC<{
  active: boolean;
  collapsed: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}> = ({ active, collapsed, icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title={collapsed ? label : undefined}
    className={`flex w-full items-center rounded-lg px-2.5 py-[7px] text-[13.5px] font-medium transition-colors ${collapsed ? 'justify-center' : 'gap-2.5'} ${
      active
        ? 'text-claude-accent'
        : 'hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover'
    }`}
  >
    <span className={`inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center ${
      active ? 'text-claude-accent' : 'text-claude-textSecondary dark:text-claude-darkTextSecondary'
    }`}>{icon}</span>
    {!collapsed && (
      <span className={`min-w-0 flex-1 truncate text-left ${
        active ? 'text-claude-accent' : 'text-claude-text dark:text-claude-darkText'
      }`}>
        {label}
      </span>
    )}
  </button>
);

const Sidebar: React.FC<SidebarProps> = ({
  onShowSettings,
  onShowAccount,
  activeView,
  onShowSkills,
  onShowCowork,
  onShowScheduledTasks,
  onShowScenarios,
  onShowConnectors,
  onNewChat,
  onOpenSecretary,
  isCollapsed,
  onToggleCollapse,
  authState,
  isMobile,
}) => {
  const dispatch = useDispatch();
  const sessions = useSelector((state: RootState) => state.cowork.sessions);
  const currentSessionId = useSelector((state: RootState) => state.cowork.currentSessionId);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [version, setVersion] = useState('');
  const [batchMode, setBatchMode] = useState(false);
  const [downloadUrls, setDownloadUrls] = useState<Record<string, string>>({});
  const [showDownloadPopover, setShowDownloadPopover] = useState(false);
  const [relayClients, setRelayClients] = useState(() => relayService.getAllClients());
  const downloadBtnRef = useRef<HTMLButtonElement>(null);
  const downloadPopoverRef = useRef<HTMLDivElement>(null);
  const [sidebarWidth, setSidebarWidth] = useState(getInitialSidebarWidth);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarWidthRef = useRef(sidebarWidth);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(sidebarWidth);
  const persistSidebarWidth = useCallback((width: number) => {
    try {
      window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(Math.round(width)));
    } catch {
      // 浏览器禁用存储时仍保留当前页面内的拖拽结果。
    }
  }, []);

  const handleResizeStart = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (isMobile || isCollapsed) return;
    event.preventDefault();
    resizeStartXRef.current = event.clientX;
    resizeStartWidthRef.current = sidebarWidthRef.current;
    setIsResizing(true);
  }, [isCollapsed, isMobile]);

  const handleResizeKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isMobile || isCollapsed || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
    event.preventDefault();
    const nextWidth = clampSidebarWidth(
      sidebarWidthRef.current + (event.key === 'ArrowRight' ? 16 : -16),
    );
    sidebarWidthRef.current = nextWidth;
    setSidebarWidth(nextWidth);
    persistSidebarWidth(nextWidth);
  }, [isCollapsed, isMobile, persistSidebarWidth]);

  useEffect(() => {
    sidebarWidthRef.current = sidebarWidth;
  }, [sidebarWidth]);

  useEffect(() => {
    if (!isResizing) return;

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handlePointerMove = (event: PointerEvent) => {
      const nextWidth = clampSidebarWidth(
        resizeStartWidthRef.current + event.clientX - resizeStartXRef.current,
      );
      sidebarWidthRef.current = nextWidth;
      setSidebarWidth(nextWidth);
    };
    const handlePointerEnd = () => {
      persistSidebarWidth(sidebarWidthRef.current);
      setIsResizing(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerEnd);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isResizing, persistSidebarWidth]);

  useEffect(() => {
    if (isMobile) return;
    const handleWindowResize = () => {
      setSidebarWidth((currentWidth) => {
        const nextWidth = clampSidebarWidth(currentWidth);
        sidebarWidthRef.current = nextWidth;
        return nextWidth;
      });
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [isMobile]);

  const phoneNumber = typeof authState?.user.phoneNumber === 'string' ? authState.user.phoneNumber.trim() : '';
  const username = typeof authState?.user.username === 'string' ? authState.user.username.trim() : '';
  const displayName = typeof authState?.user.displayName === 'string' ? authState.user.displayName.trim() : '';
  const maskedAccountIdentity = phoneNumber ? maskPhoneNumber(phoneNumber) : maskUsername(username);
  const accountInitial = (displayName || username || '?').slice(0, 1).toUpperCase();

  useEffect(() => {
    const syncRelayClients = () => setRelayClients(relayService.getAllClients());
    const unsubscribe = relayService.subscribe(syncRelayClients);
    if (authState) {
      void relayService.ensureConnected().then(syncRelayClients).catch(syncRelayClients);
    } else {
      syncRelayClients();
    }
    return unsubscribe;
  }, [authState]);


  const visibleSessions = useMemo(() => sessions.filter((session) => {
    if (session.origin !== 'desktop') return true;
    const clientId = getTargetClient(session.id);
    return Boolean(clientId && relayClients.some((client) => client.clientId === clientId && client.online));
  }), [sessions, relayClients]);

  // ── 环境选择(参考 Coding 会话列表的设备选择器)──
  // 'cloud' 云端沙箱(非 desktop 会话,默认) | 其它值 = 桌面 clientId
  //
  // 这里不再自持状态：环境选择同时决定「任务记录按哪个环境过滤」和「新建任务在哪执行」，
  // 两者本来就是同一个概念。各存各的会出现「侧边栏选了桌面设备、composer 还在显示沙箱
  // 选项」这种自相矛盾的界面，所以统一读写 cowork slice 里的 selectedDesktopClientId。
  const selectedDesktopClientId = useSelector((state: RootState) => state.cowork.selectedDesktopClientId);
  const envFilter: 'cloud' | string = selectedDesktopClientId ?? 'cloud';
  const setEnvFilter = useCallback((next: 'cloud' | string) => {
    const clientId = next === 'cloud' ? null : next;
    const deviceName = clientId
      ? relayService.getAllClients().find((c) => c.clientId === clientId)?.deviceName ?? null
      : null;
    dispatch(setSelectedDesktopClient({ clientId, deviceName }));
  }, [dispatch]);
  const [envMenuOpen, setEnvMenuOpen] = useState(false);
  const envMenuRef = useRef<HTMLDivElement>(null);
  const envBtnRef = useRef<HTMLButtonElement>(null);

  const onlineDesktops = useMemo(
    () => relayClients.filter((client) => client.platform !== 'web' && client.online),
    [relayClients],
  );

  // 手动刷新桌面会话:自助修复"web 端显示的桌面会话跟桌面本身对不上"，
  // 不用等自动重连时机、也不用刷新整个页面。
  const [isRefreshingDesktopSessions, setIsRefreshingDesktopSessions] = useState(false);
  const handleRefreshDesktopSessions = async () => {
    if (isRefreshingDesktopSessions) return;
    setIsRefreshingDesktopSessions(true);
    try {
      await relayService.refreshDesktopSessions();
      window.dispatchEvent(new CustomEvent('app:showToast', {
        detail: i18nService.t('desktopSessionsRefreshed'),
      }));
    } catch (error) {
      console.warn('[Sidebar] Failed to refresh desktop sessions:', error);
      window.dispatchEvent(new CustomEvent('app:showToast', {
        detail: i18nService.t('desktopSessionsRefreshFailed'),
      }));
    } finally {
      setIsRefreshingDesktopSessions(false);
    }
  };

  // 所选桌面下线后回退到「云端沙箱」
  useEffect(() => {
    if (envFilter === 'cloud') return;
    if (!onlineDesktops.some((client) => client.clientId === envFilter)) {
      setEnvFilter('cloud');
    }
  }, [envFilter, onlineDesktops]);

  useEffect(() => {
    if (!envMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!envMenuRef.current?.contains(target) && !envBtnRef.current?.contains(target)) {
        setEnvMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [envMenuOpen]);

  const envFilteredSessions = useMemo(() => {
    if (envFilter === 'cloud') return visibleSessions.filter((s) => s.origin !== 'desktop');
    return visibleSessions.filter((s) => s.origin === 'desktop' && getTargetClient(s.id) === envFilter);
  }, [visibleSessions, envFilter]);

  const envFilterLabel = useMemo(() => {
    if (envFilter === 'cloud') return i18nService.t('envSelectorCloudSandbox');
    const client = onlineDesktops.find((c) => c.clientId === envFilter);
    return client ? (client.deviceName || platformLabel(client.platform)) : i18nService.t('envSelectorCloudSandbox');
  }, [envFilter, onlineDesktops]);

  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) return envFilteredSessions;
    const q = searchQuery.trim().toLowerCase();
    return envFilteredSessions.filter((s) => s.title.toLowerCase().includes(q));
  }, [envFilteredSessions, searchQuery]);

  useEffect(() => {
    window.electron.appInfo.getVersion().then((v: string) => setVersion(v));
  }, []);

  useEffect(() => {
    window.electron.appInfo.getDownloadUrls().then(setDownloadUrls);
  }, []);

  useEffect(() => {
    if (!showDownloadPopover) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        downloadPopoverRef.current && !downloadPopoverRef.current.contains(e.target as Node) &&
        downloadBtnRef.current && !downloadBtnRef.current.contains(e.target as Node)
      ) {
        setShowDownloadPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadPopover]);

  useEffect(() => {
    const handleSearch = () => {
      onShowCowork();
      setIsSearchOpen(true);
    };
    window.addEventListener('cowork:shortcut:search', handleSearch);
    return () => {
      window.removeEventListener('cowork:shortcut:search', handleSearch);
    };
  }, [onShowCowork]);

  useEffect(() => {
    if (!isCollapsed) return;
    setIsSearchOpen(false);
    setBatchMode(false);
  }, [isCollapsed]);

  useEffect(() => {
    if (sessions.length === 0) setBatchMode(false);
  }, [sessions.length]);

  const getSessionSummary = (sessionId: string) => sessions.find((session) => session.id === sessionId);

  const showDesktopError = (error: unknown) => {
    window.dispatchEvent(new CustomEvent('app:showToast', {
      detail: `${i18nService.t('relayDesktopOffline')}: ${error instanceof Error ? error.message : String(error)}`,
    }));
  };

  const handleSelectSession = async (sessionId: string) => {
    onShowCowork();
    // 切到别的会话就丢掉还没发出去的技能选择——它属于选它时的那个上下文。
    // 不清的话，选完技能再点开另一个会话，那几个技能会被当成新会话本轮的技能发出去。
    if (sessionId !== currentSessionId) {
      dispatch(clearActiveSkills());
    }
    const session = getSessionSummary(sessionId);
    if (session?.origin === 'desktop') {
      const clientId = getTargetClient(sessionId);
      if (!clientId) {
        showDesktopError(new Error('Desktop session route is unavailable'));
        return;
      }
      try {
        await relayService.ensureConnected();
        await relayService.loadDesktopSession(clientId, sessionId);
      } catch (error) {
        if (error instanceof Error && error.message.startsWith('NOT_FOUND:')) {
          relayService.forgetDesktopSession(sessionId);
        } else {
          showDesktopError(error);
        }
        return;
      }
    } else {
      await coworkService.loadSession(sessionId);
    }
    if (isMobile) onToggleCollapse();
  };

  const handleDeleteSession = async (sessionId: string) => {
    const session = getSessionSummary(sessionId);
    if (session?.origin !== 'desktop') {
      await coworkService.deleteSession(sessionId);
      return;
    }
    const clientId = getTargetClient(sessionId);
    if (!clientId) {
      showDesktopError(new Error('Desktop session route is unavailable'));
      return;
    }
    try {
      await relayService.ensureConnected();
      await relayService.deleteSession(clientId, sessionId);
      relayService.forgetDesktopSession(sessionId);
    } catch (error) {
      showDesktopError(error);
    }
  };

  const handleTogglePin = async (sessionId: string, pinned: boolean) => {
    if (getSessionSummary(sessionId)?.origin === 'desktop') return;
    await coworkService.setSessionPinned(sessionId, pinned);
  };

  const handleRenameSession = async (sessionId: string, title: string) => {
    if (getSessionSummary(sessionId)?.origin === 'desktop') return;
    await coworkService.renameSession(sessionId, title);
  };

  const downloadOptions = useMemo(() => {
    const opts: { key: string; label: string; url: string }[] = [];
    if (downloadUrls.mac) opts.push({ key: 'mac', label: i18nService.t('downloadMac'), url: downloadUrls.mac });
    if (downloadUrls.win) opts.push({ key: 'win', label: i18nService.t('downloadWin'), url: downloadUrls.win });
    if (downloadUrls.linuxArm64) opts.push({ key: 'linuxArm64', label: i18nService.t('downloadLinuxArm64'), url: downloadUrls.linuxArm64 });
    if (downloadUrls.linuxX64) opts.push({ key: 'linuxX64', label: i18nService.t('downloadLinuxX64'), url: downloadUrls.linuxX64 });
    return opts;
  }, [downloadUrls]);

  const hasDownloads = true; // CLI 安装始终可用

  const handleBatchDelete = async (sessionIds: string[]) => {
    for (const id of sessionIds) {
      await handleDeleteSession(id);
    }
    setBatchMode(false);
  };

  return (
    <aside
      style={!isMobile && !isCollapsed ? { width: `${sidebarWidth}px` } : undefined}
      className={`
        ${isMobile
          ? `fixed inset-y-0 left-0 z-50 w-80 shadow-modal transform transition-transform duration-200 ease-out ${
              isCollapsed ? '-translate-x-full' : 'translate-x-0'
            }`
          : `shrink-0 sidebar-transition ${isCollapsed ? 'w-14' : ''}`
        }
        relative flex flex-col overflow-hidden bg-white dark:bg-claude-darkSurface
        ${isResizing ? 'select-none' : ''}
      `}
    >
      {!isMobile && !isCollapsed && (
        <div
          role="separator"
          aria-label="调整侧边栏宽度"
          aria-orientation="vertical"
          aria-valuemin={SIDEBAR_MIN_WIDTH}
          aria-valuemax={SIDEBAR_MAX_WIDTH}
          aria-valuenow={Math.round(sidebarWidth)}
          tabIndex={0}
          onPointerDown={handleResizeStart}
          onKeyDown={handleResizeKeyDown}
          className="group absolute inset-y-0 right-0 z-30 w-1.5 cursor-col-resize touch-none outline-none"
          title="拖拽调整侧边栏宽度"
        >
          <span className={`absolute inset-y-0 right-0 w-px transition-colors ${
            isResizing
              ? 'bg-claude-accent'
              : 'bg-transparent group-hover:bg-claude-accent/70 group-focus:bg-claude-accent/70'
          }`} />
        </div>
      )}
      <div className="pb-3">
        <div className={`space-y-0.5 ${isCollapsed ? 'px-1.5' : 'px-3'}`}>
          <div
            className={`draggable sidebar-header-drag flex items-center ${isCollapsed ? 'h-12 cursor-pointer justify-center' : 'h-[72px] justify-between gap-2 px-2'}`}
            onClick={isCollapsed ? onToggleCollapse : undefined}
            title={isCollapsed ? i18nService.t('expand') : undefined}
          >
            {isCollapsed ? (
              <SidebarToggleIcon className="h-[18px] w-[18px] text-claude-textSecondary dark:text-claude-darkTextSecondary" isCollapsed={true} />
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => onOpenSecretary?.()}
                  className="non-draggable flex min-w-0 flex-1 items-center gap-2.5 rounded-xl py-1 text-left transition-colors hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover"
                >
                  <BomateWordmark className="h-[26px] w-[99px]" />
                </button>
                <div className="non-draggable flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={onToggleCollapse}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
                    aria-label={i18nService.t('collapse')}
                  >
                    <SidebarToggleIcon className="h-[18px] w-[18px]" isCollapsed={false} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsSearchOpen(true)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
                    aria-label={i18nService.t('searchConversations')}
                  >
                    <MagnifyingGlassIcon className="h-5 w-5" />
                  </button>
                </div>
              </>
            )}
          </div>
          <CompactNavItem
            active={activeView === 'cowork'}
            collapsed={isCollapsed}
            icon={<NewChatIcon />}
            label={i18nService.t('newChat')}
            onClick={() => { onNewChat(); if (isMobile) onToggleCollapse(); }}
          />
          <CompactNavItem
            active={activeView === 'scheduledTasks'}
            collapsed={isCollapsed}
            icon={<ClockIcon />}
            label={i18nService.t('scheduledTasks')}
            onClick={() => {
              setIsSearchOpen(false);
              onShowScheduledTasks();
              if (isMobile) onToggleCollapse();
            }}
          />
          <CompactNavItem
            active={activeView === 'skills'}
            collapsed={isCollapsed}
            icon={<SkillsIcon />}
            label={i18nService.t('skills')}
            onClick={() => {
              setIsSearchOpen(false);
              onShowSkills();
              if (isMobile) onToggleCollapse();
            }}
          />
          <CompactNavItem
            active={activeView === 'scenarios'}
            collapsed={isCollapsed}
            icon={<ScenariosIcon />}
            label={i18nService.t('scenarios')}
            onClick={() => {
              setIsSearchOpen(false);
              onShowScenarios();
              if (isMobile) onToggleCollapse();
            }}
          />
          <CompactNavItem
            active={activeView === 'connectors'}
            collapsed={isCollapsed}
            icon={<ConnectorIcon />}
            label={i18nService.t('connectors')}
            onClick={() => {
              setIsSearchOpen(false);
              onShowConnectors();
              if (isMobile) onToggleCollapse();
            }}
          />


          {isCollapsed && (
            <button
              type="button"
              onClick={() => {
                onToggleCollapse();
                onShowCowork();
                requestAnimationFrame(() => {
                  setTimeout(() => searchInputRef.current?.focus(), 300);
                });
              }}
              className="w-full inline-flex items-center justify-center rounded-lg px-2.5 py-[7px] text-[13.5px] font-medium transition-colors dark:text-claude-darkTextSecondary text-claude-textSecondary hover:text-claude-text dark:hover:text-claude-darkText hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover"
              title={i18nService.t('searchConversations')}
            >
              <MagnifyingGlassIcon className="h-[18px] w-[18px] shrink-0" />
            </button>
          )}
        </div>
      </div>
      {isCollapsed && <div className="flex-1" />}
      {!isCollapsed && (
        <div className="px-2.5 pt-2">
          <div className="px-2 pb-2 flex items-center justify-between">
            <div className="text-[11px] font-medium tracking-wide dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {i18nService.t('coworkHistory')}
            </div>
            {sessions.length > 0 && !searchQuery.trim() && (
              <button
                type="button"
                onClick={() => setBatchMode(!batchMode)}
                className="text-[11px] dark:text-claude-darkTextSecondary text-claude-textSecondary hover:text-claude-accent transition-colors"
              >
                {batchMode ? i18nService.t('batchDone') : i18nService.t('batchManage')}
              </button>
            )}
          </div>
          {/* 环境选择器始终显示，桌面端离线时仍可查看状态并手动刷新。 */}
          <div className="relative px-0.5 pb-1.5 flex items-center gap-1">
              <button
                ref={envBtnRef}
                type="button"
                onClick={() => setEnvMenuOpen((v) => !v)}
                className="flex flex-1 min-w-0 items-center gap-2 rounded-lg bg-black/[0.03] px-2.5 py-1.5 text-[12.5px] text-claude-text transition-colors hover:bg-black/[0.05] dark:bg-white/[0.06] dark:text-claude-darkText dark:hover:bg-white/[0.1]"
              >
                <span className="text-claude-accent">
                  {envFilter === 'cloud'
                    ? <CloudIcon className="h-4 w-4" />
                    : <ComputerDesktopIcon className="h-4 w-4" />}
                </span>
                <span className="flex-1 truncate text-left">{envFilterLabel}</span>
                <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
              </button>
              {/* 桌面会话列表跟桌面本身对不上时(比如断线重连间隙、旧缓存残留),
                  让用户能自己点一下重新拉最新的,不用等自动同步或刷新整页。 */}
              <button
                type="button"
                onClick={handleRefreshDesktopSessions}
                disabled={isRefreshingDesktopSessions}
                title={i18nService.t('refreshDesktopSessions')}
                className="shrink-0 h-7 w-7 inline-flex items-center justify-center rounded-lg text-claude-textSecondary transition-colors hover:bg-black/[0.05] hover:text-claude-accent disabled:cursor-not-allowed disabled:opacity-50 dark:text-claude-darkTextSecondary dark:hover:bg-white/[0.1]"
              >
                <ArrowPathIcon className={`h-4 w-4 ${isRefreshingDesktopSessions ? 'animate-spin' : ''}`} />
              </button>
              {envMenuOpen && (
                <div
                  ref={envMenuRef}
                  className="absolute left-0.5 right-0.5 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-xl border border-black/[0.08] dark:border-white/10 bg-white dark:bg-claude-darkSurface py-1 shadow-lg"
                >
                  <button
                    type="button"
                    onClick={() => { setEnvFilter('cloud'); setEnvMenuOpen(false); }}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-black/[0.04] dark:hover:bg-white/[0.05] ${envFilter === 'cloud' ? 'font-medium text-claude-accent' : 'dark:text-claude-darkText text-claude-text'}`}
                  >
                    <CloudIcon className="h-4 w-4 text-claude-accent" />
                    <span className="flex-1 truncate text-left">{i18nService.t('envSelectorCloudSandbox')}</span>
                  </button>
                  <div className="mx-3 my-1 border-t border-black/[0.06] dark:border-white/10" />
                  {onlineDesktops.length > 0 ? onlineDesktops.map((client) => (
                    <button
                      key={client.clientId}
                      type="button"
                      onClick={() => { setEnvFilter(client.clientId); setEnvMenuOpen(false); }}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-black/[0.04] dark:hover:bg-white/[0.05] ${envFilter === client.clientId ? 'font-medium text-claude-accent' : 'dark:text-claude-darkText text-claude-text'}`}
                    >
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-green-500" />
                      <span className="flex-1 truncate text-left">{client.deviceName || platformLabel(client.platform)}</span>
                      <span className="text-[11px] dark:text-claude-darkTextSecondary text-claude-textSecondary">{platformLabel(client.platform)}</span>
                    </button>
                  )) : (
                    <div className="flex items-center gap-2 px-3 py-2 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
                      <ComputerDesktopIcon className="h-4 w-4 shrink-0 opacity-70" />
                      <span>{i18nService.t('relayNoDesktopOnline')}</span>
                    </div>
                  )}
                </div>
              )}
          </div>
          <div className="px-0.5 pt-1 pb-2.5">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary pointer-events-none" />
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') { setSearchQuery(''); searchInputRef.current?.blur(); } }}
                placeholder={i18nService.t('searchConversations')}
                className="w-full pl-8 pr-7 py-1.5 text-[12.5px] rounded-lg bg-black/[0.03] dark:bg-white/[0.06] dark:text-claude-darkText text-claude-text dark:placeholder-claude-darkTextSecondary placeholder-claude-textSecondary border-0 focus:outline-none focus:bg-black/[0.05] dark:focus:bg-white/[0.10] focus:ring-1 focus:ring-black/[0.06] dark:focus:ring-white/[0.10] transition-all"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded dark:text-claude-darkTextSecondary text-claude-textSecondary hover:text-claude-text dark:hover:text-claude-darkText transition-colors"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className={`flex-1 overflow-y-auto overflow-x-hidden px-2.5 pb-4 ${isCollapsed ? 'hidden' : ''}`}>
        {searchQuery.trim() && filteredSessions.length === 0 ? (
          <div className="py-6 text-center text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
            {i18nService.t('searchNoResults')}
          </div>
        ) : (
          <CoworkSessionList
            sessions={filteredSessions}
            currentSessionId={currentSessionId}
            onSelectSession={handleSelectSession}
            onDeleteSession={handleDeleteSession}
            onTogglePin={handleTogglePin}
            onRenameSession={handleRenameSession}
            batchMode={!searchQuery.trim() && batchMode}
            onBatchDelete={handleBatchDelete}
          />
        )}
      </div>
      <CoworkSearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        sessions={sessions}
        currentSessionId={currentSessionId}
        onSelectSession={handleSelectSession}
        onDeleteSession={handleDeleteSession}
        onTogglePin={handleTogglePin}
        onRenameSession={handleRenameSession}
      />
      <div className={isCollapsed ? 'px-1.5 pb-3 pt-1' : 'shrink-0 px-2 pb-1 pt-1.5'}>
        {isCollapsed ? (
          <div className="flex flex-col items-center gap-2">
            {authState ? (
              <button
                type="button"
                onClick={onShowAccount}
                className="p-2 rounded-lg dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
                title={maskedAccountIdentity}
              >
                {authState.user.avatarUrl ? (
                  <img src={authState.user.avatarUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
                ) : (
                  <div
                    className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white"
                    style={{ background: 'linear-gradient(135deg, color-mix(in srgb, var(--accent-color, #3B82F6) 70%, white) 0%, var(--accent-color, #3B82F6) 100%)' }}
                  >
                    {accountInitial}
                  </div>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="p-2 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
                title={i18nService.t('login')}
              >
                <UserCircleIcon className="h-6 w-6" />
              </button>
            )}
            {hasDownloads && (
              <div className="relative group">
                <button
                  ref={downloadBtnRef}
                  type="button"
                  onClick={() => setShowDownloadPopover(!showDownloadPopover)}
                  className="p-2 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                </button>
                {!showDownloadPopover && (
                  <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 hidden group-hover:flex items-center z-[100]">
                    <div className="w-0 h-0 border-y-[5px] border-y-transparent border-r-[5px] border-r-gray-900 dark:border-r-gray-800 shrink-0" />
                    <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap">
                      {i18nService.t('downloadDesktopApp')}
                    </div>
                  </div>
                )}
                {showDownloadPopover && (
                  <div
                    ref={downloadPopoverRef}
                    className="absolute bottom-full left-0 mb-2 w-72 rounded-xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface shadow-lg py-1.5 z-50"
                  >
                    {downloadOptions.length > 0 && (
                      <>
                        <div className="px-3 py-1.5 text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                          {i18nService.t('downloadDesktopApp')}
                        </div>
                        {downloadOptions.map((opt) => (
                          <a
                            key={opt.key}
                            href={opt.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setShowDownloadPopover(false)}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm dark:text-claude-darkText text-claude-text hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4 shrink-0 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
                            {opt.label}
                          </a>
                        ))}
                      </>
                    )}
                    <div className="my-1 border-t dark:border-claude-darkBorder border-claude-border" />
                    <CliInstallSection registry={downloadUrls.bobotCliRegistry} installBaseUrl={downloadUrls.bobotCliInstallBaseUrl} />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          authState ? (
            (() => {
              return (
            <>
            <CampaignCard slot="sidebar_card" />
            <div className="relative mb-1 flex w-full items-center gap-2 rounded-xl px-2.5 py-2.5 text-left">
              <button
                type="button"
                onClick={onShowAccount}
                className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg text-left transition-colors hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover"
                title={maskedAccountIdentity}
              >
                {authState.user.avatarUrl ? (
                  <img src={authState.user.avatarUrl} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
                ) : (
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                    style={{ background: 'linear-gradient(135deg, color-mix(in srgb, var(--accent-color, #3B82F6) 70%, white) 0%, var(--accent-color, #3B82F6) 100%)' }}
                  >
                    {accountInitial}
                  </span>
                )}
                <span className="min-w-0 flex-1 text-left">
                <div className="truncate text-[12.5px] font-medium dark:text-claude-darkText text-claude-text" title={maskedAccountIdentity}>
                  {maskedAccountIdentity}
                </div>
                <SidebarCreditBalance key={`${authState.user.id}:${authState.loggedInAt}`} />
                </span>
              </button>
              <button
                type="button"
                onClick={onShowSettings}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
                aria-label={i18nService.t('appSettings')}
              >
                <Cog6ToothIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center justify-between gap-2 px-1 py-0.5 text-[11px] leading-none text-claude-textSecondary dark:text-claude-darkTextSecondary">
              <span className="opacity-70">{version ? `BoMate v${version}` : 'BoMate'}</span>
              <a href="https://bomate.cn" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-claude-accent">
                {i18nService.t('help')}
              </a>
            </div>
            </>
              );
            })()
          ) : null
        )}
      </div>
    </aside>
  );
};

/** BoBot CLI 安装区域（嵌入下载 popover 中） */
function CliInstallSection({ registry, installBaseUrl }: { registry?: string; installBaseUrl?: string }) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const registryArg = registry ? ` --registry ${registry}` : '';
  const baseUrl = installBaseUrl || 'https://repo.boclaw.cn/artifactory/boclaw-agent/latest';

  const macLinuxCmd = `curl -fsSL ${baseUrl}/bobot-cli-install.sh | bash`;
  const windowsCmd = `irm ${baseUrl}/bobot-cli-install.ps1 | iex`;
  const npmCmd = `npm i -g @bocloud/bobot-cli${registryArg}`;

  const handleCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const CopyBtn: React.FC<{ copyKey: string; text: string }> = ({ copyKey, text }) => (
    <button
      onClick={(e) => { e.stopPropagation(); handleCopy(text, copyKey); }}
      className="ml-auto shrink-0 px-1.5 py-0.5 rounded text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
    >
      {copiedKey === copyKey ? i18nService.t('bobotCliCopied') : i18nService.t('copyToClipboard')}
    </button>
  );

  const cmdBlockClass = 'rounded-md dark:bg-claude-darkBg bg-claude-bg px-2 py-1.5 text-[11px] font-mono break-all dark:text-claude-darkText text-claude-text select-all leading-relaxed';

  return (
    <div className="px-3 py-1.5">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-1 rounded text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary hover:text-claude-text dark:hover:text-claude-darkText transition-colors"
        aria-expanded={expanded}
      >
        <span>{i18nService.t('bobotCliInstall')}</span>
        <ChevronDownIcon className={`ml-auto h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
      <div className="mt-1.5">
      {/* macOS / Linux */}
      <div className="mb-1.5">
        <div className="flex items-center gap-1 mb-0.5">
          <span className="text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">{i18nService.t('bobotCliMacLinux')}</span>
          <CopyBtn copyKey="maclinux" text={macLinuxCmd} />
        </div>
        <div className={cmdBlockClass}>{macLinuxCmd}</div>
      </div>
      {/* Windows (PowerShell) */}
      <div className="mb-1.5">
        <div className="flex items-center gap-1 mb-0.5">
          <span className="text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">{i18nService.t('bobotCliWindows')}</span>
          <CopyBtn copyKey="windows" text={windowsCmd} />
        </div>
        <div className={cmdBlockClass}>{windowsCmd}</div>
      </div>
      {/* npm 通用 */}
      <div>
        <div className="flex items-center gap-1 mb-0.5">
          <span className="text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">{i18nService.t('bobotCliNpm')}</span>
          <CopyBtn copyKey="npm" text={npmCmd} />
        </div>
        <div className={cmdBlockClass}>{npmCmd}</div>
      </div>
      </div>
      )}
    </div>
  );
}

export default Sidebar;
